import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import GetMarketplaceDisplayName from '../../../../../../../components/data-driven-logo/get-marketplace-display-name';
import ShipstationGear from '../../../../../../../pages/home-page/components/onboard-section/onboard-svgs/shipstation-gear.svg';
import DataDrivenLogo from '../../../../../../../components/data-driven-logo';

import messages from './messages';

import style from './style.module.less';

/**
 * A loading component that shows a connection between ShipStation (gear icon) and a marketplace
 * Used during the authentication process with a marketplace
 * @param {object} props - Component props
 * @param {string|number} props.marketplaceId - ID of the marketplace being connected to
 * @param {() => void} [props.onClick] - Optional function to call when the link is clicked
 * @param {boolean} [props.showHelpText] - Flag to show help text or not
 * @param {object} [props.loadingText] - Text to display while loading
 * @param {boolean} [props.useMonoAuthCredentials] - Flag to show messages when using Mono Auth credentials
 * @returns {React.JSX.Element} The rendered component
 */
const ConnectingLoader = ({
  marketplaceId,
  onClick,
  showHelpText = true,
  loadingText = messages.connecting,
  useMonoAuthCredentials = false,
}) => {
  const marketplaceInfo = GetMarketplaceDisplayName(marketplaceId);
  const marketplace = marketplaceInfo?.defaultMessage;

  return (
    <>
      <div className={style.connectingLoader}>
        <div className={style.title}>
          {useMonoAuthCredentials ? (
            <Text textType={TextType.H3} value={messages.gettingSetUp} />
          ) : (
            <Text textType={TextType.H3} value={<FormattedMessage {...loadingText} values={{ marketplace }} />} />
          )}
        </div>

        <Text
          className={style.subtitle}
          textType={TextType.Caption}
          value={useMonoAuthCredentials ? messages.mayTakeAMoment : messages.completeAuthentication}
        />

        <div className={style.iconContainer}>
          <div className={style.iconWrapper}>
            <img alt='ShipStation Gear' className={style.gearIcon} src={ShipstationGear} />
          </div>

          <div className={style.connectionLine}>
            <div className={style.line} />
            <div className={style.spinner}>
              <LoadingIndicator />
            </div>
          </div>

          <div className={style.iconWrapper}>
            <DataDrivenLogo className={style.marketplaceIcon} logoType='icon' marketplaceId={marketplaceId} />
          </div>
        </div>
      </div>

      <Text
        className={classnames(style.helpText, {
          [style.hidden]: !showHelpText,
        })}
        textType={TextType.Caption}
        value={
          <FormattedMessage
            {...messages.reopenLink}
            values={{
              link: (chunks) => (
                <Button className={style.clickHere} onClick={onClick} theme='link'>
                  <Text textType={TextType.Caption} value={chunks} />
                </Button>
              ),
              marketplace,
            }}
          />
        }
      />
    </>
  );
};

export default ConnectingLoader;
